import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/App";
import { useTranslation } from "react-i18next";
import { getCryptoIconUrl, getCryptoIconFallback } from "@/lib/cryptoIcons";
import { formatAssetAmount } from "@/lib/amountFormat";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, ArrowRightLeft } from "lucide-react";

// Extended crypto list like Crypto Bot
const allCryptos = [
  {
    currency: "USDT",
    name: "Tether",
    icon: getCryptoIconUrl("USDT"),
    price: 0.999,
    change: 0.00
  },
  {
    currency: "USDC",
    name: "USD Coin",
    icon: getCryptoIconUrl("USDC"),
    price: 0.999,
    change: 0.00
  },
  {
    currency: "TON",
    name: "Toncoin",
    icon: getCryptoIconUrl("TON"),
    price: 1.46,
    change: 1.12
  },
  {
    currency: "SOL",
    name: "Solana",
    icon: getCryptoIconUrl("SOL"),
    price: 121.54,
    change: -1.05
  },
  {
    currency: "TRX",
    name: "TRON",
    icon: getCryptoIconUrl("TRX"),
    price: 0.283,
    change: 0.00
  },
  {
    currency: "BTC",
    name: "Bitcoin",
    icon: getCryptoIconUrl("BTC"),
    price: 43250.00,
    change: 2.34
  },
  {
    currency: "ETH",
    name: "Ethereum",
    icon: getCryptoIconUrl("ETH"),
    price: 2280.00,
    change: -0.52
  },
];

export const CryptoList = () => {
  const { t } = useTranslation();
  const { balances, hideBalance, market, preferredCurrency, config } = useApp();
  const navigate = useNavigate();
  const EPS = 1e-6;

  // State for hold dialog
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdDetails, setHoldDetails] = useState(null);
  const [copied, setCopied] = useState(false);

  const currencySymbols = {
    USD: '$',
    RUB: '₽',
  };

  const getRate = (currency) => {
    if (!currency || currency === 'USD') return 1;
    if (!config) return 1;
    const key = `usd_${currency.toLowerCase()}_rate`;
    return config[key] || 1;
  };

  const formatAmount = (amount, currency, minDecimals = 2) => {
    return formatAssetAmount(amount, currency, {
      hideBalance,
      minDecimals,
    });
  };

  const formatFiat = (amount) => {
    const symbol = currencySymbols[preferredCurrency] || '$';
    if (hideBalance) return `${symbol}••••`;
    if (amount == null || Number.isNaN(amount)) return "—";

    const rate = getRate(preferredCurrency);
    const converted = Number(amount) * rate;
    return `${symbol}${converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPrice = (price) => {
    const symbol = currencySymbols[preferredCurrency] || '$';
    if (price == null || Number.isNaN(Number(price))) return "—";

    const rate = getRate(preferredCurrency);
    const converted = Number(price) * rate;

    if (converted >= 1) {
      return `${symbol}${converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `${symbol}${converted.toFixed(3)}`;
  };

  // Merge wallet balances with all cryptos
  const getCryptoData = (crypto) => {
    const walletBalance = balances.find(b => b.currency === crypto.currency);
    const marketPrice = market?.prices?.[crypto.currency];
    const marketChange = market?.changes?.[crypto.currency];
    const derivedPrice =
      marketPrice ??
      (walletBalance?.usd_rate ? Number(walletBalance.usd_rate) : null) ??
      crypto.price;
    const derivedChange =
      (typeof marketChange === "number" ? marketChange : null) ??
      crypto.change;

    const normalizedChange =
      (crypto.currency === "USDT" || crypto.currency === "USDC") && derivedChange != null && Math.abs(derivedChange) < 0.01
        ? 0.0
        : derivedChange;
    return {
      ...crypto,
      amount: walletBalance?.amount || 0,
      hold: walletBalance?.hold || 0,
      available: walletBalance?.available ?? null,
      usd_value: walletBalance?.usd_value ?? null,
      topup_hold: walletBalance?.topup_hold || 0,
      topup_hold_items: walletBalance?.topup_hold_items || [],
      price: derivedPrice,
      change: normalizedChange
    };
  };

  // Filter cryptos
  const cryptosWithData = allCryptos.map(getCryptoData);
  // Show all balances sorted by USD value descending
  const displayCryptos = cryptosWithData
    .sort((a, b) => {
      const aUsdValue = a.usd_value || 0;
      const bUsdValue = b.usd_value || 0;
      // If USD values are equal, sort by currency name for stable order
      if (bUsdValue === aUsdValue) {
        return a.currency.localeCompare(b.currency);
      }
      return bUsdValue - aUsdValue;
    });

  // Доля актива в портфеле. Показывается тонкой полосой по нижней кромке
  // строки: список отсортирован по стоимости, но из одних цифр не видно,
  // «Tether — это весь портфель» или «Tether — чуть больше остальных».
  // Полоса отвечает на это, не занимая ни строки текста.
  const portfolioUsd = displayCryptos.reduce(
    (sum, c) => sum + (Number(c.usd_value) || 0),
    0
  );
  const shareOf = (crypto) => {
    if (hideBalance || portfolioUsd <= EPS) return 0;
    const value = Number(crypto.usd_value) || 0;
    if (value <= EPS) return 0;
    return Math.min(100, (value / portfolioUsd) * 100);
  };

  const openHoldDialog = (crypto) => {
    if (!crypto?.topup_hold_items?.length) return;
    const sortedItems = [...crypto.topup_hold_items].sort((a, b) => {
      const aTs = Date.parse(String(a?.created_at || "")) || 0;
      const bTs = Date.parse(String(b?.created_at || "")) || 0;
      return bTs - aTs;
    });
    const item =
      sortedItems.find((it) => String(it?.pay_address || "").trim().length > 0) ||
      sortedItems[0];
    setHoldDetails({
      currency: crypto.currency,
      held: Number(crypto.topup_hold) || 0,
      paid: Number(item.credited_amount) || 0,
      minRequired: Number(item.min_required) || 0,
      remaining: Number(item.remaining) || 0,
      address: String(item.pay_address || "").trim()
    });
    setCopied(false);
    setHoldDialogOpen(true);
  };

  const handleCopy = async () => {
    if (!holdDetails?.address) return;
    try {
      await navigator.clipboard.writeText(holdDetails.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <div className="crypto-list-container" data-testid="crypto-list">
      {/* Header with toggle */}
      <div className="crypto-list-header">
        <span className="crypto-list-title">{t('home.assets')}</span>
        <button
          onClick={() => navigate('/exchange')}
          className="crypto-list-toggle"
          data-testid="exchange-btn"
        >
          <ArrowRightLeft className="w-4 h-4 mr-1" />
          {t('actions.swap')}
        </button>
      </div>

      {/* Crypto items */}
      <div className="crypto-list">
        {displayCryptos.map((crypto) => {
        const share = shareOf(crypto);
        const empty = (Number(crypto.usd_value) || 0) <= EPS;
        return (
          <div
            key={crypto.currency}
            className={`crypto-item${empty ? " is-empty" : ""}`}
            data-testid={`crypto-${crypto.currency.toLowerCase()}`}
          >
            {share > 0.5 && (
              <span
                className="crypto-share"
                style={{ width: `${share.toFixed(2)}%` }}
                aria-hidden="true"
              />
            )}
            <img
              src={crypto.icon}
              alt={crypto.currency}
              className="crypto-icon"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getCryptoIconFallback(crypto.currency);
              }}
            />
            <div className="crypto-info">
              <div className="crypto-name">{crypto.name}</div>
              <div className="crypto-price-row">
                <span className="crypto-price">{formatPrice(crypto.price)}</span>
                {(crypto.currency !== "USDT" && crypto.currency !== "USDC") && (
                  <span className={`crypto-change ${crypto.change >= 0 ? 'positive' : 'negative'}`}>
                    {crypto.change == null || Number.isNaN(Number(crypto.change))
                      ? "—"
                      : `${crypto.change >= 0 ? "+" : ""}${Number(crypto.change).toFixed(2)}%`}
                  </span>
                )}
              </div>
            </div>
            <div className="crypto-balance">
              <div className="crypto-balance-amount">
                {formatAmount(crypto.amount, crypto.currency)} {crypto.currency}
              </div>
              {(() => {
                const netHold = Math.max(
                  Number(crypto.hold || 0) - Number(crypto.topup_hold || 0),
                  0
                );
                if (netHold <= EPS) return null;
                return (
                  <div className="text-xs text-amber-600">
                    {t('home.hold', {
                      amount: formatAmount(netHold, crypto.currency, 2),
                      currency: crypto.currency,
                    })}
                  </div>
                );
              })()}
              {Number(crypto.topup_hold) > EPS && (
                <button
                  type="button"
                  onClick={() => openHoldDialog(crypto)}
                  className="text-xs text-amber-600 text-left hover:underline"
                >
                  {t('home.topup_hold', {
                    amount: formatAmount(crypto.topup_hold, crypto.currency, 2),
                    currency: crypto.currency,
                  })}
                </button>
              )}
              <div className="crypto-balance-usd">
                {formatFiat(crypto.usd_value)}
              </div>
            </div>
          </div>
        );
        })}
      </div>

      <Dialog open={holdDialogOpen} onOpenChange={setHoldDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('home.topup_hold_title')}</DialogTitle>
            <DialogDescription>
              {holdDetails
                  ? t('home.topup_hold_body', {
                  paid: formatAmount(holdDetails.paid, holdDetails.currency, 2),
                  min: formatAmount(holdDetails.minRequired, holdDetails.currency, 2),
                  remaining: formatAmount(holdDetails.remaining, holdDetails.currency, 2),
                  currency: holdDetails.currency,
                })
                : ""}
            </DialogDescription>
          </DialogHeader>
          {holdDetails?.address && (
            <div className="mt-3">
              <div className="text-xs text-muted-foreground mb-1">{t('home.topup_hold_address')}</div>
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                <div className="text-xs font-mono break-all flex-1 text-slate-900 select-text">
                  {holdDetails.address}
                </div>
                <button
                  onClick={handleCopy}
                  className="p-2 rounded-md bg-white border border-gray-200"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button
              className="w-full h-11"
              onClick={() => {
                setHoldDialogOpen(false);
                navigate("/topup");
              }}
            >
              {t('home.topup_hold_action')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CryptoList;
